import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { EnumMensageriaDirecao } from '../../../../../shared/enums';
import { TiposDirecaoMensagemService } from '../../../../../shared/services/api/c4-model/TiposDirecaoMensagemService';
import { ContainerService } from '../../../../../shared/services/api/c4-model/ContainerService';

type TipoDirecaoMensagem = {
  id: number;
  nome: string;
};

type TipoContainer = {
  id: number;
  nome: string;
};

type AcessoContainer = {
  id?: string;
  mensageriaId?: string;
  tipo: number;
  direcao: number;
};

type Row = {
  id: string;
  tipo: number;
  direcao: number;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: AcessoContainer[];
  onContainersChange?: (containers: AcessoContainer[]) => void;
  mensageriaId?: string;
};

const convertToRows = (containers: AcessoContainer[]): Row[] => {
  return (containers || []).map((item, index) => ({
    id: item.id || `temp-${Date.now()}-${index}`,
    tipo: item.tipo,
    direcao: item.direcao,
  }));
};

const convertToContainers = (rows: Row[]): AcessoContainer[] => {
  return (rows || [])
    .filter((row) => !row.isNew && !row.isEditing)
    .map((row) => ({
      id: row.id.startsWith('temp-') ? undefined : row.id,
      tipo: row.tipo,
      direcao: row.direcao,
    }));
};

export const ListaContainerTopico: React.FC<Props> = ({
  initialRows = [],
  onContainersChange,
  mensageriaId,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingValues, setEditingValues] = useState<{
    [key: string]: {
      tipo: number;
      direcao: number;
    };
  }>({});

  const [tiposDirecaoMensagem, setTiposDirecaoMensagem] = useState<
    TipoDirecaoMensagem[]
  >([]);
  const [tiposContainer, setTiposContainer] = useState<TipoContainer[]>([]);

  const getTipoContainerNome = (id: number): string => {
    const tipo = tiposContainer.find((t) => t.id === id);
    return tipo ? tipo.nome : `Tipo ${id}`;
  };

  useEffect(() => {
    TiposDirecaoMensagemService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposDirecaoMensagem(data);
      }
    });

    // Carregar tipos de container
    ContainerService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposContainer(data);
      }
    });
  }, []);

  // Função para obter o nome da direção de mensagem
  const getTipoDirecaoNome = (id: number): string => {
    const tipoDirecao = tiposDirecaoMensagem.find((t) => t.id === id);
    return tipoDirecao?.nome || `Direção ${id}`;
  };

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    setRows(rows.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));

    setEditingValues({
      ...editingValues,
      [id]: {
        tipo: row.tipo,
        direcao: row.direcao,
      },
    });
  };

  const handleCancelEdit = (id: string) => {
    setRows(
      rows
        .map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const editingValue = editingValues[id];
    if (!editingValue) {
      return;
    }

    const newRows = rows.map((r) =>
      r.id === id
        ? {
          ...r,
          id: r.isNew
            ? `container-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`
            : r.id,
          tipo: editingValue.tipo,
          direcao: editingValue.direcao,
          isEditing: false,
          isNew: false,
        }
        : r
    );

    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onContainersChange) {
      const containersResult = convertToContainers(newRows);
      onContainersChange(containersResult);
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onContainersChange) {
      const containersResult = convertToContainers(newRows);
      onContainersChange(containersResult);
    }
  };

  const handleAddNew = () => {
    const newId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const newRow: Row = {
      id: newId,
      tipo: 0, // EnumContainerTipo.PARCEL
      direcao: EnumMensageriaDirecao.PUBLICA,
      isEditing: true,
      isNew: true,
    };

    setRows([...rows, newRow]);
    setEditingValues({
      ...editingValues,
      [newId]: {
        tipo: 0, // EnumContainerTipo.PARCEL
        direcao: EnumMensageriaDirecao.PUBLICA,
      },
    });
  };

  const handleEditingValueChange = (id: string, field: string, value: any) => {
    setEditingValues({
      ...editingValues,
      [id]: {
        ...editingValues[id],
        [field]: value,
      },
    });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Container</TableCell>
              <TableCell>Direção</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhum container cadastrado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {row.isEditing ? (
                      <>
                        <Tooltip title="Salvar">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(row.id)}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelEdit(row.id)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.isEditing ? (
                      <FormControl fullWidth size="small">
                        <Select
                          variant="outlined"
                          value={editingValues[row.id]?.tipo || row.tipo}
                          onChange={(e) =>
                            handleEditingValueChange(
                              row.id,
                              'tipo',
                              Number(e.target.value)
                            )
                          }
                        >
                          {tiposContainer.map((tipo) => (
                            <MenuItem key={tipo.id} value={tipo.id}>
                              {tipo.nome}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      getTipoContainerNome(row.tipo)
                    )}
                  </TableCell>
                  <TableCell>
                    {row.isEditing ? (
                      <FormControl fullWidth size="small">
                        <Select
                          variant="outlined"
                          value={editingValues[row.id]?.direcao || row.direcao}
                          onChange={(e) =>
                            handleEditingValueChange(
                              row.id,
                              'direcao',
                              Number(e.target.value)
                            )
                          }
                        >
                          {tiposDirecaoMensagem.map((tipoDirecao) => (
                            <MenuItem
                              key={tipoDirecao.id}
                              value={tipoDirecao.id}
                            >
                              {tipoDirecao.nome}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      getTipoDirecaoNome(row.direcao)
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
